import React from "react";

import {
  DEFAULT_SIDEBAR,
  arrayToMap,
  capitalizeString,
} from "@excalidraw/common";
import {
  getColorUpdate,
  mutateElement,
  showSelectedShapeActions,
  ShapeCache,
} from "@excalidraw/element";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import { desktopCopy } from "../desktopFeatures";

import { actionShortcuts, actionToggleStats } from "../actions";
import { TunnelsContext, useInitializeTunnels } from "../context/tunnels";
import { UIAppStateContext } from "../context/ui-appState";
import { useAtom, useAtomValue } from "../editor-jotai";
import { t } from "../i18n";
import { getScrollToContentState, getSelectedElements } from "../scene";
import {
  getColorTargetAppStateUpdates,
  resolveColorTarget,
} from "../actions/colorTargets";

import { DesktopPurchaseDialog } from "./DesktopPurchaseDialog";

import { SelectedShapeActions, CompactShapeActions } from "./Actions";
import { LoadingMessage } from "./LoadingMessage";
import { PasteChartDialog } from "./PasteChartDialog";
import Footer from "./footer/Footer";
import { isSidebarDockedAtom } from "./Sidebar/Sidebar";
import MainMenu from "./main-menu/MainMenu";
import { ActiveConfirmDialog } from "./ActiveConfirmDialog";
import { useAppProps, useEditorInterface, useStylesPanelMode } from "./App";
import { OverwriteConfirmDialog } from "./OverwriteConfirm/OverwriteConfirm";
import { LibraryIcon, ExportIcon, HelpIcon } from "./icons";
import { DefaultSidebar } from "./DefaultSidebar";
import { TTDDialog } from "./TTDDialog/TTDDialog";
import { Stats } from "./Stats";
import ElementLinkDialog from "./ElementLinkDialog";
import { ErrorDialog } from "./ErrorDialog";
import { EyeDropper, activeEyeDropperAtom } from "./EyeDropper";
import { HelpDialog } from "./HelpDialog";
import { ImageExportDialog } from "./ImageExportDialog";
import { JSONExportDialog } from "./JSONExportDialog";
import { Toast } from "./Toast";
import { Toolbar } from "./Toolbar";
import {
  ViewportStatusBadge,
  ViewportStatusBorder,
} from "./ViewportStatusFrame/ViewportStatusFrame";
import { workspaceText } from "./workspaceText";

import type { ColorDefaultKey } from "../actions/colorTargets";
import type { ActionManager } from "../actions/manager";
import type { Language } from "../i18n";
import type {
  AppProps,
  AppState,
  ExcalidrawProps,
  BinaryFiles,
  UIAppState,
  AppClassProperties,
} from "../types";

interface LayerUIProps {
  actionManager: ActionManager;
  appState: UIAppState;
  files: BinaryFiles;
  canvas: HTMLCanvasElement;
  setAppState: React.Component<any, AppState>["setState"];
  elements: readonly NonDeletedExcalidrawElement[];
  onLockToggle: () => void;
  onPenModeToggle: AppClassProperties["togglePenMode"];
  showExitZenModeBtn: boolean;
  langCode: Language["code"];
  renderTopLeftUI?: ExcalidrawProps["renderTopLeftUI"];
  renderTopRightUI?: ExcalidrawProps["renderTopRightUI"];
  renderCustomStats?: ExcalidrawProps["renderCustomStats"];
  UIOptions: AppProps["UIOptions"];
  onExportImage: AppClassProperties["onExportImage"];
  renderWelcomeScreen: boolean;
  children?: React.ReactNode;
  app: AppClassProperties;
  defaultUIEnabled: boolean;
  zoomUIEnabled: boolean;
  scrollBackToContentUIEnabled: boolean;
  generateLinkForSelection?: AppProps["generateLinkForSelection"];
  currentUserControls?: ExcalidrawProps["currentUserControls"];
}
const DefaultMainMenu = ({
  UIOptions,
}: {
  UIOptions: AppProps["UIOptions"];
}) => (
  <MainMenu __fallback>
    <MainMenu.DefaultItems.LoadScene />
    <MainMenu.DefaultItems.SaveToActiveFile />
    {UIOptions.canvasActions.export && <MainMenu.DefaultItems.Export />}
    {UIOptions.canvasActions.saveAsImage && (
      <MainMenu.DefaultItems.SaveAsImage />
    )}
    <MainMenu.Separator />
    <MainMenu.DefaultItems.SearchMenu />
    <MainMenu.DefaultItems.Help />
    <MainMenu.DefaultItems.ToggleTheme allowSystemTheme={false} />
    <MainMenu.DefaultItems.ChangeCanvasBackground />
    <MainMenu.DefaultItems.ClearCanvas />
  </MainMenu>
);

const DefaultOverwriteConfirmDialog = () => (
  <OverwriteConfirmDialog __fallback>
    <OverwriteConfirmDialog.Actions.SaveToDisk />
    <OverwriteConfirmDialog.Actions.ExportToImage />
  </OverwriteConfirmDialog>
);

const LayerUI = ({
  actionManager,
  appState,
  files,
  setAppState,
  elements,
  canvas,
  onLockToggle,
  onPenModeToggle,
  showExitZenModeBtn,
  renderTopLeftUI,
  renderTopRightUI,
  renderCustomStats,
  UIOptions,
  onExportImage,
  renderWelcomeScreen,
  children,
  app,
  defaultUIEnabled,
  zoomUIEnabled,
  scrollBackToContentUIEnabled,
  generateLinkForSelection,
  currentUserControls,
  langCode,
}: LayerUIProps) => {
  const editorInterface = useEditorInterface();
  const appProps = useAppProps();
  const compact = useStylesPanelMode() === "compact";
  const tunnels = useInitializeTunnels();
  const TunnelsJotaiProvider = tunnels.tunnelsJotai.Provider;
  const [eyeDropperState, setEyeDropperState] = useAtom(activeEyeDropperAtom);
  const docked = useAtomValue(isSidebarDockedAtom);
  const reserveSidebar =
    appState.openSidebar && docked && editorInterface.canFitSidebar;
  const mobile = editorInterface.formFactor === "phone";
  const showInspector =
    defaultUIEnabled &&
    !appState.viewModeEnabled &&
    showSelectedShapeActions(appState, elements);
  const showStats =
    defaultUIEnabled &&
    appState.stats.open &&
    !appState.zenModeEnabled &&
    !appState.viewModeEnabled;
  const renderJSONExportDialog = () => {
    if (!UIOptions.canvasActions.export) {
      return null;
    }

    return (
      <JSONExportDialog
        elements={elements}
        appState={appState}
        files={files}
        actionManager={actionManager}
        exportOpts={UIOptions.canvasActions.export}
        canvas={canvas}
        setAppState={setAppState}
      />
    );
  };

  const renderImageExportDialog = () => {
    if (
      !UIOptions.canvasActions.saveAsImage ||
      appState.openDialog?.name !== "imageExport"
    ) {
      return null;
    }

    return (
      <ImageExportDialog
        elements={elements}
        appState={appState}
        files={files}
        actionManager={actionManager}
        onExportImage={onExportImage}
        onCloseRequest={() => setAppState({ openDialog: null })}
        name={app.getName()}
      />
    );
  };

  return (
    <UIAppStateContext.Provider value={appState}>
      <TunnelsJotaiProvider>
        <TunnelsContext.Provider value={tunnels}>
          {children}
          {defaultUIEnabled && <DefaultMainMenu UIOptions={UIOptions} />}
          {appState.openDialog?.name === "desktopPurchase" && (
            <DesktopPurchaseDialog
              app={app}
              feature={appState.openDialog.feature}
              onClose={() => setAppState({ openDialog: null })}
            />
          )}
          <DefaultOverwriteConfirmDialog />
          {appState.openDialog?.name === "ttd" && <TTDDialog __fallback />}
          <div
            className="studio-workspace layer-ui__wrapper"
            data-zen={appState.zenModeEnabled}
            style={
              reserveSidebar
                ? { width: "calc(100% - var(--right-sidebar-width))" }
                : undefined
            }
          >
            <header className="studio-header">
              <div className="studio-identity">
                <span className="studio-mark" aria-hidden="true">
                  <svg viewBox="0 0 28 28" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="5" />
                    <path d="M10 10h15v15H10V10Z" />
                  </svg>
                </span>
                <div>
                  <strong>Whiteboard</strong>
                  <span>{workspaceText(langCode, "workspace")}</span>
                </div>
              </div>
              <div className="studio-header-divider" />
              <div className="studio-file-menu">
                <tunnels.MainMenuTunnel.Out />
                {renderTopLeftUI?.(mobile, appState)}
              </div>
              <span className="studio-document-name">
                {workspaceText(langCode, "untitled")}
              </span>
              <div className="studio-header-actions">
                {app.props.desktopPurchaseUrl && (
                  <a
                    className="desktop-header-purchase"
                    href={app.props.desktopPurchaseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="desktop-header-purchase"
                  >
                    {desktopCopy(langCode).buy}
                  </a>
                )}
                <tunnels.DefaultSidebarTriggerTunnel.Out />
                {/* Collaboration presence (the "who's here" UserList fed by
                    `appState.collaborators`) was removed with multiplayer
                    collaboration. `appState.collaborators` survives as passive
                    scene metadata so host-supplied scene data still restores. */}
                {renderTopRightUI?.(mobile, appState)}
                {defaultUIEnabled && (
                  <button
                    type="button"
                    className="studio-header-help"
                    title={t("helpDialog.title")}
                    aria-label={t("helpDialog.title")}
                    onClick={() => actionManager.executeAction(actionShortcuts)}
                  >
                    {HelpIcon}
                  </button>
                )}
                {defaultUIEnabled && !appState.viewModeEnabled && (
                  <button
                    type="button"
                    className="studio-library-button sidebar-trigger"
                    data-testid="studio-library-trigger"
                    aria-label={capitalizeString(t("toolBar.library"))}
                    aria-pressed={
                      appState.openSidebar?.name === DEFAULT_SIDEBAR.name
                    }
                    onClick={() =>
                      app.requestDesktopFeature("library") ||
                      setAppState({
                        openSidebar:
                          appState.openSidebar?.name === DEFAULT_SIDEBAR.name
                            ? null
                            : {
                                name: DEFAULT_SIDEBAR.name,
                                tab: DEFAULT_SIDEBAR.defaultTab,
                              },
                      })
                    }
                  >
                    {LibraryIcon}
                    <span>{capitalizeString(t("toolBar.library"))}</span>
                    {app.props.desktopPurchaseUrl && (
                      <span className="desktop-feature-badge">EXE</span>
                    )}
                  </button>
                )}
                {defaultUIEnabled && UIOptions.canvasActions.saveAsImage && (
                  <button
                    type="button"
                    className="studio-export"
                    data-testid="studio-export"
                    onClick={() =>
                      setAppState({ openDialog: { name: "imageExport" } })
                    }
                  >
                    {ExportIcon}
                    <span>{workspaceText(langCode, "export")}</span>
                  </button>
                )}
              </div>
            </header>
            {renderWelcomeScreen && <tunnels.WelcomeScreenCenterTunnel.Out />}
            {defaultUIEnabled &&
              !appState.viewModeEnabled &&
              appState.openDialog?.name !== "elementLinkSelector" && (
                <Toolbar
                  app={app}
                  appState={appState}
                  setAppState={setAppState}
                  UIOptions={UIOptions}
                  onPenModeToggle={onPenModeToggle}
                  onLockToggle={onLockToggle}
                  heading={null}
                />
              )}
            {showInspector && (
              <details
                className="studio-inspector"
                open={!mobile}
                data-viewport-ui="side"
                data-viewport-ui-name="stylesPanel"
              >
                <summary>
                  <span>{workspaceText(langCode, "styles")}</span>
                  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="m4 6 4 4 4-4" />
                  </svg>
                </summary>
                <div className="studio-inspector-content">
                  {compact ? (
                    <CompactShapeActions
                      appState={appState}
                      elementsMap={app.scene.getNonDeletedElementsMap()}
                      renderAction={actionManager.renderAction}
                      app={app}
                      setAppState={setAppState}
                    />
                  ) : (
                    <SelectedShapeActions
                      appState={appState}
                      elementsMap={app.scene.getNonDeletedElementsMap()}
                      renderAction={actionManager.renderAction}
                      app={app}
                    />
                  )}
                </div>
              </details>
            )}
            <Footer
              appState={appState}
              actionManager={actionManager}
              showExitZenModeBtn={showExitZenModeBtn}
              renderWelcomeScreen={renderWelcomeScreen}
              defaultUIEnabled={defaultUIEnabled}
              zoomUIEnabled={zoomUIEnabled}
            />
            {showStats && (
              <Stats
                app={app}
                onClose={() => actionManager.executeAction(actionToggleStats)}
                renderCustomStats={renderCustomStats}
              />
            )}
            <div className="studio-notices">
              {appState.toast && (
                <Toast
                  message={appState.toast.message}
                  onClose={() => setAppState({ toast: null })}
                  duration={appState.toast.duration}
                  closable={appState.toast.closable}
                />
              )}
              {!appState.toast &&
                scrollBackToContentUIEnabled &&
                appState.scrolledOutside && (
                  <button
                    type="button"
                    className="scroll-back-to-content"
                    onClick={() =>
                      setAppState((state) => ({
                        ...getScrollToContentState(elements, state),
                      }))
                    }
                  >
                    {t("buttons.scrollBackToContent")}
                  </button>
                )}
              {appProps.viewportStatusFrame?.label && (
                <ViewportStatusBadge
                  label={appProps.viewportStatusFrame.label}
                  border={appProps.viewportStatusFrame.border}
                />
              )}
            </div>
          </div>
          {appProps.viewportStatusFrame?.border && (
            <ViewportStatusBorder
              border={appProps.viewportStatusFrame.border}
            />
          )}
          {defaultUIEnabled && <DefaultSidebar __fallback />}
          {defaultUIEnabled && appState.isLoading && (
            <LoadingMessage delay={250} />
          )}
          {defaultUIEnabled && appState.errorMessage && (
            <ErrorDialog onClose={() => setAppState({ errorMessage: null })}>
              {appState.errorMessage}
            </ErrorDialog>
          )}
          {defaultUIEnabled &&
            eyeDropperState &&
            editorInterface.formFactor !== "phone" && (
              <EyeDropper
                colorPickerType={eyeDropperState.colorPickerType}
                onCancel={() => {
                  setEyeDropperState(null);
                }}
                onChange={(
                  colorPickerType,
                  color,
                  selectedElements,
                  { altKey },
                ) => {
                  if (
                    colorPickerType !== "elementBackground" &&
                    colorPickerType !== "elementStroke"
                  ) {
                    return;
                  }

                  const property =
                    altKey && eyeDropperState.swapPreviewOnAlt
                      ? colorPickerType === "elementBackground"
                        ? "strokeColor"
                        : "backgroundColor"
                      : colorPickerType === "elementBackground"
                      ? "backgroundColor"
                      : "strokeColor";

                  if (selectedElements.length) {
                    const elementsMap = arrayToMap(elements);
                    // a note's visible text is its label, so stroke picks
                    // include bound labels
                    const targets = getSelectedElements(elements, appState, {
                      includeBoundTextElement: property === "strokeColor",
                    });
                    for (const element of targets) {
                      mutateElement(
                        element,
                        elementsMap,
                        getColorUpdate(element, property, color, elementsMap),
                      );
                      ShapeCache.delete(element);
                    }
                    app.scene.triggerUpdate();
                  } else {
                    setAppState(
                      getColorTargetAppStateUpdates(
                        resolveColorTarget(appState, elements, property),
                        color,
                      ) as Pick<AppState, ColorDefaultKey>,
                    );
                  }
                }}
                onSelect={(color, event) => {
                  setEyeDropperState((state) => {
                    return state?.keepOpenOnAlt && event.altKey ? state : null;
                  });
                  eyeDropperState?.onSelect?.(color, event);
                }}
              />
            )}
          {appState.openDialog?.name === "help" && (
            <HelpDialog
              onClose={() => {
                setAppState({ openDialog: null });
              }}
            />
          )}
          <ActiveConfirmDialog />
          {defaultUIEnabled &&
            appState.openDialog?.name === "elementLinkSelector" && (
              <ElementLinkDialog
                sourceElementId={appState.openDialog.sourceElementId}
                onClose={() => {
                  setAppState({
                    openDialog: null,
                  });
                }}
                scene={app.scene}
                appState={appState}
                generateLinkForSelection={generateLinkForSelection}
              />
            )}
          <tunnels.OverwriteConfirmDialogTunnel.Out />
          {renderImageExportDialog()}
          {renderJSONExportDialog()}
          {defaultUIEnabled && appState.openDialog?.name === "charts" && (
            <PasteChartDialog
              data={appState.openDialog.data}
              rawText={appState.openDialog.rawText}
              onClose={() =>
                setAppState({
                  openDialog: null,
                })
              }
            />
          )}
        </TunnelsContext.Provider>
      </TunnelsJotaiProvider>
    </UIAppStateContext.Provider>
  );
};

export default React.memo(LayerUI);
